import React, { useCallback, useEffect, useState } from "react";
import MappingStatusBadge from "./marketplace/MappingStatusBadge";
import BulkApproveButton from "./marketplace/BulkApproveButton";
import {
    approveSuggestedRozetkaAttributeMappings,
    createRozetkaAttributeMapping,
    createRozetkaAttributeValueMapping,
    getRozetkaAttributeFeedFields,
    getRozetkaAttributeMappingSummary,
    getRozetkaAttributeMappings,
    getRozetkaAttributeValueMappings,
    getRozetkaReferenceAttributes,
    suggestRozetkaAttributeMappings,
    syncRozetkaAttributes,
    updateRozetkaAttributeMapping,
    updateRozetkaAttributeValueMapping,
} from "../api/rozetkaApi";

// Аналог PromAttributeMappingPanel.jsx, но: (1) без загрузки XML/MD файла --
// схема Rozetka приходит живым API-вызовом (см. reference_service.py), не
// статической документацией, как у Prom; (2) использует общие примитивы
// MappingStatusBadge/BulkApproveButton и тему .rozetka-page (RozetkaPage.css)
// вместо голого текста/инлайн-кнопок.
const RozetkaAttributeMappingPanel = ({ accountId, categoryId, categoryName = "Категория Rozetka", onStatus }) => {
    const [schema, setSchema] = useState([]);
    const [fields, setFields] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [summary, setSummary] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [values, setValues] = useState({});
    const [manual, setManual] = useState({ definitionId: "", rzAttributeId: "" });
    const [valueDrafts, setValueDrafts] = useState({});

    const load = useCallback(async () => {
        if (!accountId || !categoryId) return;
        const [schemaRows, fieldRows, mappingRows, summaryRow] = await Promise.all([
            getRozetkaReferenceAttributes(accountId, categoryId),
            getRozetkaAttributeFeedFields(accountId, categoryId),
            getRozetkaAttributeMappings(accountId, categoryId),
            getRozetkaAttributeMappingSummary(accountId, categoryId),
        ]);
        setSchema(schemaRows || []);
        setFields(fieldRows || []);
        setMappings(mappingRows || []);
        setSummary(summaryRow);
    }, [accountId, categoryId]);

    useEffect(() => {
        load().catch((error) => onStatus(error?.response?.data?.detail || "Не удалось загрузить mapping характеристик"));
    }, [load, onStatus]);

    const runSync = async () => {
        const result = await syncRozetkaAttributes(accountId, categoryId);
        onStatus(`Синхронизировано характеристик: ${result.items_count}.`);
        await load();
    };

    const runSuggestions = async () => {
        const result = await suggestRozetkaAttributeMappings(accountId, categoryId);
        onStatus(`Создано предложений: свойств ${result.attribute_suggestions}, значений ${result.value_suggestions}.`);
        await load();
    };

    const toggleValues = async (mapping) => {
        if (!expanded[mapping.id]) {
            setValues({ ...values, [mapping.id]: await getRozetkaAttributeValueMappings(accountId, categoryId, mapping.id) });
        }
        setExpanded({ ...expanded, [mapping.id]: !expanded[mapping.id] });
    };

    const updateMapping = async (mapping, status) => {
        await updateRozetkaAttributeMapping(accountId, categoryId, mapping.id, { status });
        await load();
    };

    const updateValue = async (mapping, value, status) => {
        await updateRozetkaAttributeValueMapping(accountId, categoryId, mapping.id, value.id, { status });
        setValues({ ...values, [mapping.id]: await getRozetkaAttributeValueMappings(accountId, categoryId, mapping.id) });
    };

    const createManualValue = async (mapping) => {
        const draft = valueDrafts[mapping.id] || {};
        await createRozetkaAttributeValueMapping(accountId, categoryId, mapping.id, {
            source_value: draft.sourceValue,
            rz_value_id: Number(draft.rzValueId),
            status: "approved",
        });
        setValues({ ...values, [mapping.id]: await getRozetkaAttributeValueMappings(accountId, categoryId, mapping.id) });
        setValueDrafts({ ...valueDrafts, [mapping.id]: { sourceValue: "", rzValueId: "" } });
    };

    const createManual = async () => {
        await createRozetkaAttributeMapping(accountId, categoryId, {
            definition_id: Number(manual.definitionId),
            rz_attribute_id: Number(manual.rzAttributeId),
            status: "approved",
        });
        setManual({ definitionId: "", rzAttributeId: "" });
        await load();
    };

    const mappedDefinitionIds = new Set(mappings.map((row) => row.definition.id));

    return (
        <section className="rzm-card">
            <div>
                <h2>
                    Характеристики Rozetka: {categoryId} — {categoryName}
                </h2>
                <p className="rzm-hint">Справочник и mappings принадлежат выбранному аккаунту.</p>
            </div>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                <button
                    type="button"
                    className="rzm-btn rzm-btn-primary"
                    onClick={() => runSync().catch((error) => onStatus(error?.response?.data?.detail || "Не удалось синхронизировать характеристики"))}
                >
                    Синхронизировать из Rozetka
                </button>
                <button
                    type="button"
                    className="rzm-btn"
                    onClick={() => runSuggestions().catch((error) => onStatus(error?.response?.data?.detail || "Не удалось создать предложения"))}
                >
                    Предложить сопоставления
                </button>
                <BulkApproveButton
                    label="Подтвердить все свойства"
                    onApprove={() => approveSuggestedRozetkaAttributeMappings(accountId, categoryId, "attributes")}
                    onResult={(result) => {
                        onStatus(`Подтверждено свойств: ${result.approved}.`);
                        load();
                    }}
                    onError={(error) => onStatus(error?.response?.data?.detail || "Не удалось подтвердить свойства")}
                />
                <BulkApproveButton
                    label="Подтвердить все значения"
                    onApprove={() => approveSuggestedRozetkaAttributeMappings(accountId, categoryId, "values")}
                    onResult={(result) => {
                        onStatus(`Подтверждено значений: ${result.approved}.`);
                        load();
                    }}
                    onError={(error) => onStatus(error?.response?.data?.detail || "Не удалось подтвердить значения")}
                />
                <button
                    type="button"
                    className="rzm-btn"
                    onClick={() => load().catch((error) => onStatus(error?.response?.data?.detail || "Не удалось обновить данные"))}
                >
                    Обновить
                </button>
            </div>
            {summary && (
                <p className="rzm-hint">
                    Rozetka: {summary.imported_attributes} свойств / {summary.imported_values} значений · Mapping: {summary.approved_attributes}/
                    {summary.mapped_attributes} свойств, {summary.approved_values}/{summary.mapped_values} значений подтверждено
                </p>
            )}
            <details>
                <summary style={{ cursor: "pointer", fontWeight: 700 }}>Схема Rozetka ({schema.length})</summary>
                <table className="rzm-table" style={{ marginTop: "8px" }}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Характеристика UA / RU</th>
                            <th>Тип</th>
                            <th>Ед.</th>
                            <th>Значения</th>
                        </tr>
                    </thead>
                    <tbody>
                        {schema.map((row) => (
                            <tr key={row.id}>
                                <td>{row.rz_attribute_id}</td>
                                <td>
                                    {row.name_ua}
                                    <br />
                                    <small>{row.name_ru}</small>
                                </td>
                                <td>{row.value_type}</td>
                                <td>{row.unit_ua || "—"}</td>
                                <td>{row.values?.length || 0}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </details>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(200px,1fr) minmax(200px,1fr) auto", gap: "8px", alignItems: "end" }}>
                <select className="rzm-inp" value={manual.definitionId} onChange={(event) => setManual({ ...manual, definitionId: event.target.value })}>
                    <option value="">Свойство master catalog</option>
                    {fields
                        .filter((row) => row.definition && !mappedDefinitionIds.has(row.definition.id))
                        .map((row) => (
                            <option key={row.definition.id} value={row.definition.id}>
                                {row.definition.name_ua}
                            </option>
                        ))}
                </select>
                <select className="rzm-inp" value={manual.rzAttributeId} onChange={(event) => setManual({ ...manual, rzAttributeId: event.target.value })}>
                    <option value="">Характеристика Rozetka</option>
                    {schema
                        .filter((row) => row.is_active)
                        .map((row) => (
                            <option key={row.id} value={row.id}>
                                {row.name_ua} ({row.rz_attribute_id})
                            </option>
                        ))}
                </select>
                <button
                    type="button"
                    className="rzm-btn rzm-btn-primary"
                    disabled={!manual.definitionId || !manual.rzAttributeId}
                    onClick={() => createManual().catch((error) => onStatus(error?.response?.data?.detail || "Не удалось создать mapping"))}
                >
                    Связать вручную
                </button>
            </div>
            <div>
                <h3>Сопоставления свойств</h3>
                <table className="rzm-table">
                    <thead>
                        <tr>
                            <th>Master catalog</th>
                            <th>Rozetka</th>
                            <th>Метод</th>
                            <th>Статус</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mappings.map((row) => (
                            <React.Fragment key={row.id}>
                                <tr>
                                    <td>{row.definition.name_ua}</td>
                                    <td>{row.rz_attribute.name_ua}</td>
                                    <td>
                                        {row.match_method || "—"} {row.confidence != null ? `(${Math.round(row.confidence * 100)}%)` : ""}
                                    </td>
                                    <td>
                                        <MappingStatusBadge status={row.status} />
                                    </td>
                                    <td>
                                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                            <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => updateMapping(row, "approved")}>
                                                Подтвердить
                                            </button>
                                            <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => updateMapping(row, "ignored")}>
                                                Игнорировать
                                            </button>
                                            {["singleselect", "multiselect"].includes(row.rz_attribute.value_type) && (
                                                <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => toggleValues(row)}>
                                                    {expanded[row.id] ? "Скрыть значения" : "Значения"}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {expanded[row.id] && (
                                    <tr>
                                        <td colSpan="5">
                                            {(values[row.id] || []).map((value) => (
                                                <div key={value.id} style={{ display: "flex", gap: "8px", padding: "5px 0", alignItems: "center" }}>
                                                    <span>
                                                        {value.source_value} → {value.rz_value.title_ua || value.rz_value.title_ru}
                                                    </span>
                                                    <MappingStatusBadge status={value.status} />
                                                    <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => updateValue(row, value, "approved")}>
                                                        Подтвердить
                                                    </button>
                                                    <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => updateValue(row, value, "ignored")}>
                                                        Игнорировать
                                                    </button>
                                                </div>
                                            ))}
                                            <div style={{ display: "grid", gridTemplateColumns: "minmax(160px,1fr) minmax(220px,1fr) auto", gap: "8px", marginTop: "8px" }}>
                                                <select
                                                    className="rzm-inp"
                                                    value={valueDrafts[row.id]?.sourceValue || ""}
                                                    onChange={(event) =>
                                                        setValueDrafts({ ...valueDrafts, [row.id]: { ...(valueDrafts[row.id] || {}), sourceValue: event.target.value } })
                                                    }
                                                >
                                                    <option value="">Значение master catalog</option>
                                                    {(fields.find((field) => field.definition && field.definition.id === row.definition.id)?.values || []).map(
                                                        (sourceValue) => (
                                                            <option key={sourceValue} value={sourceValue}>
                                                                {sourceValue}
                                                            </option>
                                                        )
                                                    )}
                                                </select>
                                                <select
                                                    className="rzm-inp"
                                                    value={valueDrafts[row.id]?.rzValueId || ""}
                                                    onChange={(event) =>
                                                        setValueDrafts({ ...valueDrafts, [row.id]: { ...(valueDrafts[row.id] || {}), rzValueId: event.target.value } })
                                                    }
                                                >
                                                    <option value="">Значение Rozetka</option>
                                                    {row.rz_attribute.values.map((item) => (
                                                        <option key={item.id} value={item.id}>
                                                            {item.title_ua || item.title_ru}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button"
                                                    className="rzm-btn rzm-btn-primary"
                                                    disabled={!valueDrafts[row.id]?.sourceValue || !valueDrafts[row.id]?.rzValueId}
                                                    onClick={() =>
                                                        createManualValue(row).catch((error) => onStatus(error?.response?.data?.detail || "Не удалось связать значение"))
                                                    }
                                                >
                                                    Связать вручную
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default RozetkaAttributeMappingPanel;
